import React, { useState, useEffect, useRef } from 'react';
import { DigitalEmployee } from '../types';
import TeslaEmployeeCard from './TeslaEmployeeCard';

interface SkillSearchProps {
  employees: DigitalEmployee[];
}

const SkillSearch: React.FC<SkillSearchProps> = ({ employees }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState<DigitalEmployee[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allSkills, setAllSkills] = useState<string[]>([]);
  const [suggestedSkills, setSuggestedSkills] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Extract all unique skills from employees
  useEffect(() => {
    const skills = new Set<string>();
    employees.forEach(employee => {
      employee.skills.forEach(skill => {
        skills.add(skill);
      });
    });
    setAllSkills(Array.from(skills));
  }, [employees]);

  // Filter employees based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees([]);
      setSuggestedSkills([]);
      return;
    }

    // Filter skills for suggestions
    const lowerSearchTerm = searchTerm.toLowerCase();
    const matchingSkills = allSkills.filter(skill => 
      skill.toLowerCase().includes(lowerSearchTerm)
    );
    setSuggestedSkills(matchingSkills);

    // Filter employees based on skills
    const filtered = employees.filter(employee => 
      employee.skills.some(skill => 
        skill.toLowerCase().includes(lowerSearchTerm)
      )
    );
    
    setFilteredEmployees(filtered);
  }, [searchTerm, employees, allSkills]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setShowResults(true);
    
    // Simulate search delay for visual effect
    setTimeout(() => {
      setIsSearching(false);
    }, 800);
  };

  const handleSkillClick = (skill: string) => {
    setSearchTerm(skill);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="skill-search-container">
      <div className="search-header">
        <h2 className="search-title">Find <span className="text-gradient">AI Employees</span> by Skills</h2>
        <p className="search-subtitle">Discover the perfect digital employee for your specific needs</p>
      </div>
      
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by skill (e.g., JavaScript, Design, Marketing)"
            className="search-input"
          />
          <button type="submit" className="search-button">
            {isSearching ? (
              <div className="search-spinner"></div>
            ) : (
              <span className="search-icon">🔍</span>
            )}
          </button>
        </div>
        
        {suggestedSkills.length > 0 && searchTerm && !showResults && (
          <div className="skill-suggestions">
            <div className="suggestions-title">Suggested Skills:</div>
            <div className="suggestions-list">
              {suggestedSkills.map((skill, index) => (
                <div 
                  key={index} 
                  className="suggestion-item"
                  onClick={() => handleSkillClick(skill)}
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        )}
      </form>
      
      {showResults && (
        <div className="search-results">
          {isSearching ? (
            <div className="searching-message">
              <div className="search-spinner large"></div>
              <p>Searching for AI employees with {searchTerm} skills...</p>
            </div>
          ) : (
            <>
              <div className="results-header">
                <h3>Search Results</h3>
                <span className="results-count">
                  {filteredEmployees.length} {filteredEmployees.length === 1 ? 'employee' : 'employees'} found
                </span>
              </div>
              
              {filteredEmployees.length > 0 ? (
                <div className="results-grid">
                  {filteredEmployees.map(employee => (
                    <TeslaEmployeeCard 
                      key={employee.id} 
                      employee={employee} 
                      className="search-result-card"
                    />
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <p>No AI employees found with "{searchTerm}" skills</p>
                  <p className="no-results-subtitle">Try searching for a different skill</p>
                  <button 
                    className="clear-search-button"
                    onClick={() => {
                      setSearchTerm('');
                      setShowResults(false);
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                      }
                    }}
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      <div className="popular-skills">
        <div className="popular-skills-title">Popular Skills:</div>
        <div className="popular-skills-list">
          {allSkills.slice(0, 8).map((skill, index) => (
            <div 
              key={index} 
              className="popular-skill-tag"
              onClick={() => {
                setSearchTerm(skill);
                setShowResults(false);
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }}
            >
              {skill}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkillSearch;
